import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import tokenService from '../services/tokenService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      if (tokenService.isAuthenticated()) {
        try {
          // Verify token by getting user profile
          const response = await authAPI.getProfile();
          if (response.success) {
            setUser(response.data.user);
          } else {
            // Token is invalid, clear it
            setUser(null);
            tokenService.clearTokens();
          }
        } catch (error) {
          console.error('Token verification failed:', error);
          // Clear token without calling logout to avoid infinite loop
          setUser(null);
          tokenService.clearTokens();
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (username, password, totp) => {
    try {
      const payload = { username, password };
      if (totp) payload.totp = totp;
      const response = await authAPI.login(payload);

      if (response.success) {
        const { user, token } = response.data;
        setUser(user);
        tokenService.setTokens(token);
        tokenService.setUser(user);
        return { success: true };
      } else {
        // Backend signals a second factor is needed for this account.
        return { success: false, message: response.message, twoFactorRequired: !!response.twoFactorRequired };
      }
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: error.message || 'Login failed'
      };
    }
  };

  const register = async (username, email, password, acceptTerms) => {
    try {
      const response = await authAPI.register({ username, email, password, acceptTerms });
      
      if (response.success) {
        const { user, token } = response.data;
        setUser(user);
        tokenService.setTokens(token);
        tokenService.setUser(user);
        return { success: true };
      } else {
        return { success: false, message: response.message };
      }
    } catch (error) {
      console.error('Registration error:', error);
      return { 
        success: false, 
        message: error.message || 'Registration failed' 
      };
    }
  };

  // Complete a Google sign-in: the backend callback has already set the
  // httpOnly refresh cookie, so exchange it for an access token, then hydrate
  // the full user profile.
  const completeGoogleLogin = async () => {
    try {
      await tokenService.refreshAccessToken(); // uses the refresh cookie
      const response = await authAPI.getProfile();
      if (response.success) {
        setUser(response.data.user);
        tokenService.setUser(response.data.user);
        return { success: true };
      }
      return { success: false, message: 'Could not load your profile' };
    } catch (error) {
      console.error('Google login completion failed:', error);
      return { success: false, message: 'Google sign-in failed' };
    }
  };

  const logout = async () => {
    try {
      await tokenService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
    }
  };

  const logoutAll = async () => {
    try {
      await tokenService.logoutAll();
    } catch (error) {
      console.error('Logout all error:', error);
    } finally {
      setUser(null);
    }
  };

  const updateProfile = async (updates) => {
    try {
      const response = await authAPI.updateProfile(updates);
      
      if (response.success) {
        setUser(response.data.user);
        tokenService.setUser(response.data.user);
        return { success: true, message: response.message };
      } else {
        return { success: false, message: response.message };
      }
    } catch (error) {
      console.error('Profile update error:', error);
      return { 
        success: false, 
        message: error.message || 'Profile update failed' 
      };
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    logoutAll,
    updateProfile,
    completeGoogleLogin,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

